import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

export interface SeoConfig {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  type?: string;
  robots?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly siteName = 'Balkishan Dhaker';
  private readonly siteUrl = 'https://balkishan-dhaker.web.app';
  private readonly defaultImage = `${this.siteUrl}/assets/icon/logo.png`;
  private readonly isBrowser: boolean;

  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  update(config: SeoConfig, path = this.document.location?.pathname ?? '/'): void {
    const title = `${config.title} | ${this.siteName}`;
    const description = config.description;
    const keywords = config.keywords ?? 'Balkishan Dhaker, Angular developer, Ionic developer, Fullstack developer, portfolio';
    const robots = config.robots ?? 'index, follow';
    const image = this.absoluteUrl(config.image ?? this.defaultImage);
    const canonicalUrl = this.absoluteUrl(path);

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'keywords', content: keywords });
    this.meta.updateTag({ name: 'robots', content: robots });
    this.meta.updateTag({ name: 'author', content: this.siteName });
    this.meta.updateTag({ property: 'og:type', content: config.type ?? 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: this.siteName });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    this.setCanonicalUrl(canonicalUrl);
    this.setStructuredData(canonicalUrl);
  }

  private absoluteUrl(pathOrUrl: string): string {
    if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
      return pathOrUrl;
    }

    return `${this.siteUrl}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;
  }

  private setCanonicalUrl(url: string): void {
    let link = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  private setStructuredData(canonicalUrl: string): void {
    const schemaId = 'structured-data-person';
    let script = this.document.getElementById(schemaId) as HTMLScriptElement | null;

    if (!script) {
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = schemaId;
      this.document.head.appendChild(script);
    }

    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: this.siteName,
      url: canonicalUrl,
      image: this.defaultImage,
      jobTitle: 'Fullstack Developer',
      sameAs: [
        'https://github.com/baldhaker',
        'https://www.linkedin.com/in/baldhaker/',
        'https://dribbble.com/baldhaker',
        'https://stackoverflow.com/users/19172501/balkishan-dhaker'
      ]
    });

    if (!this.isBrowser) {
      return;
    }
  }
}
